import React, { useState } from 'react';
import { adminService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Plus, Edit2, Trash2, ChevronDown, ChevronUp, Save,
  AlertCircle, Loader2, ToggleLeft, ToggleRight,
  PenLine, X, CheckCircle2, BookOpen, GripVertical,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Canvas Activity Form ──────────────────────────────────────
const CanvasActivityForm = ({ stageId, existing, onSaved, onCancel }) => {
  // Safely parse allowedFileTypes — DB stores as JSON string '["pdf","docx"]' or array
  const parseFileTypes = (val) => {
    if (!val) return 'pdf, docx';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed.join(', ') : val;
      } catch { return val; }
    }
    return 'pdf, docx';
  };

  const [form, setForm] = useState({
    title:            existing?.title           ?? '',
    description:      existing?.description     ?? '',
    instructions:     existing?.instructions    ?? '',
    activityType:     existing?.activityType    ?? 'ASSIGNMENT',
    maxScore:         existing?.maxScore        ?? 100,
    passingScore:     existing?.passingScore    ?? 60,
    allowedFileTypes: parseFileTypes(existing?.allowedFileTypes),
    dueInDays:        existing?.dueInDays       ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.instructions.trim()) {
      toast.error('Title and instructions are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        maxScore:        Number(form.maxScore),
        passingScore:    Number(form.passingScore),
        dueInDays:       form.dueInDays ? Number(form.dueInDays) : null,
        allowedFileTypes: form.allowedFileTypes.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (existing?.id) {
        await adminService.updateCanvasActivity(existing.id, payload);
        toast.success('Canvas activity updated');
      } else {
        await adminService.createCanvasActivity(stageId, payload);
        toast.success('Canvas activity created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save canvas activity');
    } finally {
      setSaving(false);
    }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-amber-800 uppercase tracking-widest">
          {existing ? 'Edit Canvas Activity' : 'Add Canvas Activity'}
        </p>
        <button onClick={onCancel} className="text-amber-500 hover:text-amber-700">
          <X size={14} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label-xs">Activity Title *</label>
          <input value={form.title} onChange={f('title')} placeholder="e.g. Submit your business problem statement"
            className="input-sm w-full" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-xs">Description</label>
          <input value={form.description} onChange={f('description')} placeholder="Brief description shown to client"
            className="input-sm w-full" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-xs">Instructions * <span className="text-forest-400 normal-case font-normal">(what the client must do)</span></label>
          <textarea value={form.instructions} onChange={f('instructions')} rows={5}
            placeholder="Describe exactly what the client needs to submit, what to cover, format requirements, etc."
            className="input-sm w-full resize-none" />
        </div>
        <div>
          <label className="label-xs">Activity Type</label>
          <select value={form.activityType} onChange={f('activityType')} className="input-sm w-full bg-white">
            <option value="ASSIGNMENT">Assignment</option>
            <option value="PROJECT">Project</option>
            <option value="REFLECTION">Reflection</option>
            <option value="QUIZ">Quiz</option>
          </select>
        </div>
        <div>
          <label className="label-xs">Allowed File Types</label>
          <input value={form.allowedFileTypes} onChange={f('allowedFileTypes')} placeholder="pdf, docx, pptx"
            className="input-sm w-full" />
          <p className="text-[10px] text-forest-400 mt-1">Comma-separated, e.g: pdf, docx, jpg</p>
        </div>
        <div>
          <label className="label-xs">Max Score</label>
          <input type="number" value={form.maxScore} onChange={f('maxScore')} min={1} max={1000}
            className="input-sm w-full" />
        </div>
        <div>
          <label className="label-xs">Passing Score</label>
          <input type="number" value={form.passingScore} onChange={f('passingScore')} min={1} max={1000}
            className="input-sm w-full" />
        </div>
        <div>
          <label className="label-xs">Due in Days <span className="text-forest-400 normal-case font-normal">(optional)</span></label>
          <input type="number" value={form.dueInDays} onChange={f('dueInDays')} min={1} placeholder="e.g. 7"
            className="input-sm w-full" />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="btn-primary text-xs py-2 px-4 disabled:opacity-60 flex items-center gap-1.5">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {existing ? 'Update Activity' : 'Save Activity'}
        </button>
        <button onClick={onCancel}
          className="text-xs py-2 px-4 border border-cream-200 rounded-full text-forest-600 hover:border-forest-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Stage Row ─────────────────────────────────────────────────
const StageRow = ({ stage, onDeleted, onUpdated }) => {
  const [editing,      setEditing]      = useState(false);
  const [name,         setName]         = useState(stage.name);
  const [description,  setDescription]  = useState(stage.description ?? '');
  const [saving,       setSaving]       = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [showCanvas,   setShowCanvas]   = useState(false);
  const [canvasForm,   setCanvasForm]   = useState(null); // null | 'add' | 'edit'
  const [canvas,       setCanvas]       = useState(stage.canvasActivity ?? null);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Stage name is required'); return; }
    setSaving(true);
    try {
      await adminService.updateStage(stage.id, { name, description });
      toast.success('Stage updated');
      setEditing(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update stage');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${stage.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminService.deleteStage(stage.id);
      toast.success('Stage deleted');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete stage');
    } finally { setDeleting(false); }
  };

  const handleDeleteCanvas = async () => {
    if (!window.confirm('Delete this canvas activity?')) return;
    try {
      await adminService.deleteCanvasActivity(canvas.id);
      setCanvas(null);
      setCanvasForm(null);
      toast.success('Canvas activity deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete canvas activity');
    }
  };

  const handleCanvasSaved = async () => {
    try {
      const res = await adminService.getStageCanvas(stage.id);
      const activity = res.data?.data?.activity ?? res.data?.activity ?? res.data ?? null;
      setCanvas(activity);
    } catch (err) {
      // Fallback: reload page to get fresh data
      console.warn('Could not refresh canvas:', err);
    }
    setCanvasForm(null);
  };

  return (
    <div className="border border-cream-200 rounded-xl overflow-hidden bg-white">
      {/* Stage header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-cream-50">
        <div className="w-6 h-6 bg-forest-700 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {stage.order}
        </div>

        {editing ? (
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            <input value={name} onChange={e => setName(e.target.value)}
              className="input-sm flex-1 min-w-48" placeholder="Stage name" />
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="input-sm flex-1 min-w-48" placeholder="Stage description" />
            <button onClick={handleSave} disabled={saving}
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
            </button>
            <button onClick={() => setEditing(false)}
              className="text-xs py-1.5 px-3 border border-cream-200 rounded-full text-forest-600">
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <span className="font-sans font-semibold text-sm text-forest-900">{stage.name}</span>
            {stage.description && (
              <p className="text-xs text-forest-400 truncate mt-0.5">{stage.description}</p>
            )}
          </div>
        )}

        {!editing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Canvas indicator */}
            <button onClick={() => setShowCanvas(!showCanvas)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-all ${
                canvas
                  ? 'bg-forest-50 border-forest-200 text-forest-700'
                  : 'bg-cream-50 border-cream-200 text-forest-400 hover:border-forest-300'
              }`}>
              <PenLine size={11} />
              {canvas ? 'Activity ✓' : 'Add Activity'}
            </button>
            <button onClick={() => setEditing(true)}
              className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
              <Edit2 size={14} />
            </button>
            <button onClick={handleDelete} disabled={deleting}
              className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
          </div>
        )}
      </div>

      {/* Canvas activity section */}
      {showCanvas && (
        <div className="border-t border-cream-100 p-4 space-y-3">
          {canvas && !canvasForm ? (
            <div className="bg-forest-50 border border-forest-100 rounded-xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <PenLine size={13} className="text-forest-600 flex-shrink-0" />
                    <span className="font-sans font-semibold text-sm text-forest-900">{canvas.title}</span>
                    <span className="text-[10px] bg-forest-100 text-forest-600 px-2 py-0.5 rounded-full">
                      {canvas.activityType}
                    </span>
                  </div>
                  {canvas.description && (
                    <p className="text-xs text-forest-500 ml-5">{canvas.description}</p>
                  )}
                  <div className="flex gap-3 text-xs text-forest-400 mt-2 ml-5">
                    <span>Max: {canvas.maxScore}</span>
                    <span>Pass: {canvas.passingScore}</span>
                    {canvas.dueInDays && <span>Due: {canvas.dueInDays} days</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={async () => {
                      try {
                        const res = await adminService.getStageCanvas(stage.id);
                        const full = res.data?.data?.activity ?? res.data?.activity ?? res.data ?? canvas;
                        setCanvas(full);
                      } catch { /* use existing canvas data */ }
                      setCanvasForm('edit');
                    }}
                    className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-white transition-colors">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={handleDeleteCanvas}
                    className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {canvas.instructions && (
                <div className="bg-white border border-forest-100 rounded-lg p-3 ml-5">
                  <p className="text-xs text-forest-600 whitespace-pre-line leading-relaxed">
                    {canvas.instructions.length > 200
                      ? canvas.instructions.slice(0, 200) + '...'
                      : canvas.instructions}
                  </p>
                </div>
              )}
            </div>
          ) : !canvasForm ? (
            <div className="text-center py-4 border-2 border-dashed border-cream-200 rounded-xl">
              <BookOpen size={24} className="mx-auto mb-2 text-forest-300" />
              <p className="text-xs text-forest-400 mb-3">No activity for this stage yet</p>
              <button onClick={() => setCanvasForm('add')}
                className="text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-4 py-2 rounded-full transition-colors">
                + Add Canvas Activity
              </button>
            </div>
          ) : null}

          {canvasForm && (
            <CanvasActivityForm
              stageId={stage.id}
              existing={canvasForm === 'edit' ? canvas : null}
              onSaved={handleCanvasSaved}
              onCancel={() => setCanvasForm(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Level Card ────────────────────────────────────────────────
const LevelCard = ({ level, onUpdated, onDeleted }) => {
  const [expanded,    setExpanded]    = useState(false);
  const [editing,     setEditing]     = useState(false);
  const [toggling,    setToggling]    = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [addingStage, setAddingStage] = useState(false);
  const [stageName,   setStageName]   = useState('');
  const [stageDesc,   setStageDesc]   = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [stages,      setStages]      = useState(level.stages ?? []);
  const [editForm,    setEditForm]    = useState({
    name:        level.name,
    description: level.description,
  });
  const [savingLevel,      setSavingLevel]      = useState(false);
  // ── Stage drag & drop ────────────────────────────────────────
  const [dragStageIdx,      setDragStageIdx]      = useState(null);
  const [reorderingStages,  setReorderingStages]  = useState(false);

  const handleStageDragStart = (idx) => setDragStageIdx(idx);
  const handleStageDragOver  = (e, idx) => {
    e.preventDefault();
    if (dragStageIdx === null || dragStageIdx === idx) return;
    const updated = [...stages];
    const [moved] = updated.splice(dragStageIdx, 1);
    updated.splice(idx, 0, moved);
    setStages(updated);
    setDragStageIdx(idx);
  };
  const handleStageDrop = async (e) => {
    e.preventDefault();
    if (dragStageIdx === null) return;
    setDragStageIdx(null);
    setReorderingStages(true);
    try {
      await adminService.reorderStages(level.id, stages.map(s => s.id));
      toast.success('Stages reordered!');
    } catch {
      toast.error('Could not save stage order');
      setStages(level.stages ?? []);
    } finally { setReorderingStages(false); }
  };
  const handleStageDragEnd = () => setDragStageIdx(null);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await adminService.toggleLevelStatus(level.id);
      toast.success(`Level ${level.isActive ? 'deactivated' : 'activated'}`);
      onUpdated();
    } catch (err) {
      toast.error('Could not toggle status');
    } finally { setToggling(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${level.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminService.deleteLevel(level.id);
      toast.success('Level deleted');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete level — it may have client progress');
    } finally { setDeleting(false); }
  };

  const handleSaveLevel = async () => {
    if (!editForm.name.trim()) { toast.error('Level name required'); return; }
    setSavingLevel(true);
    try {
      await adminService.updateLevel(level.id, editForm);
      toast.success('Level updated');
      setEditing(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update level');
    } finally { setSavingLevel(false); }
  };

  const handleAddStage = async () => {
    if (!stageName.trim()) { toast.error('Stage name is required'); return; }
    setSavingStage(true);
    try {
      const res = await adminService.createStage(level.id, { name: stageName, description: stageDesc });
      const newStage = res.data?.data?.stage ?? res.data?.stage ?? { id: Date.now(), name: stageName, description: stageDesc, order: stages.length + 1, canvasActivity: null };
      setStages(prev => [...prev, newStage]);
      setStageName('');
      setStageDesc('');
      setAddingStage(false);
      toast.success('Stage added');
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add stage');
    } finally { setSavingStage(false); }
  };

  const refreshStages = async () => {
    try {
      const res = await adminService.getStages(level.id);
      setStages(res.data?.data?.stages ?? res.data?.stages ?? []);
    } catch {}
    onUpdated();
  };

  return (
    <div className={`border-2 rounded-2xl overflow-hidden transition-all ${level.isActive ? 'border-forest-200' : 'border-cream-200'}`}
      style={{ borderTopColor: level.color, borderTopWidth: 4 }}>

      {/* Level header */}
      <div className="p-5 bg-white">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-serif font-bold flex-shrink-0"
            style={{ background: level.color }}>
            {level.order}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-2">
                <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                  className="input-sm w-full font-semibold" placeholder="Level name" />
                <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  className="input-sm w-full resize-none" rows={2} placeholder="Description" />
                <div className="flex gap-2">
                  <button onClick={handleSaveLevel} disabled={savingLevel}
                    className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1">
                    {savingLevel ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="text-xs py-1.5 px-3 border border-cream-200 rounded-full text-forest-600">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-serif font-semibold text-forest-900 text-lg">{level.name}</h3>
                <p className="text-xs text-forest-500 mt-0.5 line-clamp-2">{level.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-forest-400">
                  <span>{stages.length} stage{stages.length !== 1 ? 's' : ''}</span>
                  <span>{level._count?.expertLevels ?? 0} expert{(level._count?.expertLevels ?? 0) !== 1 ? 's' : ''}</span>
                  <span className={`font-medium ${level.isActive ? 'text-forest-600' : 'text-rust-400'}`}>
                    {level.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </>
            )}
          </div>

          {!editing && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={handleToggle} disabled={toggling}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors"
                title={level.isActive ? 'Deactivate' : 'Activate'}>
                {toggling ? <Loader2 size={16} className="animate-spin" />
                  : level.isActive ? <ToggleRight size={20} className="text-forest-600" />
                  : <ToggleLeft size={20} />}
              </button>
              <button onClick={() => setEditing(true)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
                <Edit2 size={15} />
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              </button>
              <button onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stages */}
      {expanded && (
        <div className="border-t border-cream-100 bg-cream-50 p-5 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest">Stages</p>
            <button onClick={() => setAddingStage(!addingStage)}
              className="flex items-center gap-1.5 text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
              <Plus size={12} /> Add Stage
            </button>
          </div>

          {stages.length === 0 && !addingStage && (
            <div className="text-center py-8 text-forest-400">
              <BookOpen size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No stages yet</p>
              <p className="text-xs mt-1">Add stages to define the client's journey through this level</p>
            </div>
          )}

          {/* Stage drag hint */}
          {stages.length > 1 && (
            <div className="flex items-center gap-1.5 text-xs text-forest-300 px-1">
              <GripVertical size={12} />
              <span>Drag stages to reorder</span>
              {reorderingStages && <span className="flex items-center gap-1 text-forest-500 ml-1"><Loader2 size={10} className="animate-spin" /> Saving...</span>}
            </div>
          )}
          {stages.map((stage, idx) => (
            <div
              key={stage.id}
              draggable
              onDragStart={() => handleStageDragStart(idx)}
              onDragOver={e => handleStageDragOver(e, idx)}
              onDrop={e => handleStageDrop(e)}
              onDragEnd={handleStageDragEnd}
              className={`relative transition-all ${dragStageIdx === idx ? 'opacity-50' : ''}`}>
              <div className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 hover:bg-black/5 rounded-l-xl transition-colors">
                <GripVertical size={13} className="text-forest-300 hover:text-forest-500 transition-colors" />
              </div>
              <div className="pl-6">
                <StageRow
                  stage={stage}
                  onDeleted={refreshStages}
                  onUpdated={refreshStages}
                />
              </div>
            </div>
          ))}

          {/* Add stage form */}
          {addingStage && (
            <div className="bg-white border border-forest-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-forest-700 uppercase tracking-widest">New Stage</p>
              <input value={stageName} onChange={e => setStageName(e.target.value)}
                placeholder="Stage name e.g. Stage 1 — Idea Discovery"
                className="input-sm w-full" />
              <textarea value={stageDesc} onChange={e => setStageDesc(e.target.value)}
                placeholder="Brief description of what this stage covers"
                className="input-sm w-full resize-none" rows={2} />
              <div className="flex gap-2">
                <button onClick={handleAddStage} disabled={savingStage}
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 disabled:opacity-60">
                  {savingStage ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Add Stage
                </button>
                <button onClick={() => { setAddingStage(false); setStageName(''); setStageDesc(''); }}
                  className="text-xs py-2 px-4 border border-cream-200 rounded-full text-forest-600">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Create Level Form ─────────────────────────────────────────
const CreateLevelForm = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({ name: '', description: '', targetAudience: '', color: '#10B981' });
  const [saving, setSaving] = useState(false);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleCreate = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      toast.error('Name and description are required'); return;
    }
    setSaving(true);
    try {
      await adminService.createLevel({ ...form, objectives: [], targetAudience: form.targetAudience || 'All entrepreneurs' });
      toast.success('Level created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create level');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border-2 border-forest-200 rounded-2xl p-6 space-y-4">
      <h3 className="font-serif font-semibold text-forest-900 text-lg">Create New Level</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label-xs">Level Name *</label>
          <input value={form.name} onChange={f('name')} placeholder="e.g. Level 2 — Commercialise"
            className="input-sm w-full" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-xs">Description *</label>
          <textarea value={form.description} onChange={f('description')} rows={3}
            placeholder="What is this level about?" className="input-sm w-full resize-none" />
        </div>
        <div>
          <label className="label-xs">Target Audience</label>
          <input value={form.targetAudience} onChange={f('targetAudience')} placeholder="Who is this level for?"
            className="input-sm w-full" />
        </div>
        <div>
          <label className="label-xs">Level Color</label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.color} onChange={f('color')}
              className="w-10 h-9 rounded-lg border border-cream-200 cursor-pointer" />
            <input value={form.color} onChange={f('color')} className="input-sm flex-1" />
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleCreate} disabled={saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Create Level
        </button>
        <button onClick={onCancel}
          className="text-sm py-2.5 px-5 border border-cream-200 rounded-full text-forest-600 hover:border-forest-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminLevelsPage = () => {
  const [showCreate, setShowCreate] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const { data, loading, error } = useApi(
    () => adminService.getAllLevels(),
    [refetchKey],
  );

  const rawLevels = Array.isArray(data)
    ? data
    : Array.isArray(data?.levels) ? data.levels
    : Array.isArray(data?.data)   ? data.data : [];

  // ── Drag & drop for levels ────────────────────────────────────
  const [levels,       setLevels]       = React.useState([]);
  const [dragLevelIdx, setDragLevelIdx] = React.useState(null);
  const [reordering,   setReordering]   = React.useState(false);

  React.useEffect(() => { if (rawLevels.length) setLevels(rawLevels); }, [data]);

  const handleLevelDragStart = (idx) => {
    setDragLevelIdx(idx);
  };
  const handleLevelDragOver = (e, idx) => {
    e.preventDefault();
    if (dragLevelIdx === null || dragLevelIdx === idx) return;
    const updated = [...levels];
    const [moved] = updated.splice(dragLevelIdx, 1);
    updated.splice(idx, 0, moved);
    setLevels(updated);
    setDragLevelIdx(idx);
  };
  const handleLevelDrop = async (e) => {
    e.preventDefault();
    if (dragLevelIdx === null) return;
    setDragLevelIdx(null);
    setReordering(true);
    try {
      await adminService.reorderLevels(levels.map(l => l.id));
      toast.success('Levels reordered!');
    } catch (err) {
      toast.error('Could not save order');
      setLevels(rawLevels);
    } finally { setReordering(false); }
  };
  const handleLevelDragEnd = () => {
    // Only clear the drag index — do NOT save here (onDrop handles saving)
    setDragLevelIdx(null);
  };

  const refresh = () => setRefetchKey(k => k + 1);

  return (
    <DashboardLayout title="Level Management">
      <style>{`
        .input-sm { @apply px-3 py-2 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white; }
        .label-xs { @apply block text-xs font-medium text-forest-700 mb-1; }
      `}</style>

      <div className="max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-forest-500">
            {loading ? '...' : `${levels.length} level${levels.length !== 1 ? 's' : ''} configured`}
          </p>
          <button onClick={() => setShowCreate(!showCreate)}
            className="btn-primary flex items-center gap-2">
            <Plus size={16} /> Create Level
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <CreateLevelForm
            onCreated={() => { setShowCreate(false); refresh(); }}
            onCancel={() => setShowCreate(false)}
          />
        )}

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load levels — API may be unreachable.</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1,2].map(i => (
              <div key={i} className="h-32 bg-white border-2 border-cream-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-forest-400 mb-1">
            <GripVertical size={13} />
            <span>Drag levels to reorder</span>
            {reordering && <span className="flex items-center gap-1 text-forest-600"><Loader2 size={11} className="animate-spin" /> Saving order...</span>}
          </div>
          <div className="space-y-3">
            {levels.map((level, idx) => (
              <div
                key={level.id}
                draggable
                onDragStart={() => handleLevelDragStart(idx)}
                onDragOver={e => handleLevelDragOver(e, idx)}
                onDrop={e => handleLevelDrop(e)}
                onDragEnd={handleLevelDragEnd}
                className={`relative transition-all ${dragLevelIdx === idx ? 'opacity-50 scale-[0.99]' : 'opacity-100'}`}>
                {/* Drag handle — sits on left edge */}
                <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 rounded-l-2xl hover:bg-black/5 transition-colors">
                  <GripVertical size={16} className="text-forest-300 hover:text-forest-500" />
                </div>
                {/* Level card with left padding to make room for handle */}
                <div className="pl-8">
                  <LevelCard
                    level={level}
                    onUpdated={refresh}
                    onDeleted={refresh}
                  />
                </div>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminLevelsPage;